import React, { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { NotFoundPage } from 'src/pages';
import WrapperLayout from 'src/components/ui/layouts/wrapper-layout';
import { GlobalErrorFallback } from 'src/components/error-boundary/global-error-boundary';
import { errorHandlingService } from 'src/services/error-handling';
import { Logger } from 'src/utils/logger';
import { clearCachesAndReload } from 'src/utils/cache-utils';
import { getRouteErrorRecovery, getRouteErrorView, toError } from './route-error-element.logic';

const logger = Logger.createScopedLogger('RouteErrorElement');

/**
 * Router `errorElement`: 404s keep the Not Found page, anything else (render crash, failed
 * lazy chunk) shows the generic error UI with a working refresh. GlobalErrorFallback signals
 * PAGE_READY itself so the native overlay lifts.
 */
export const RouteErrorElement: React.FC = () => {
  const routeError = useRouteError();
  const view = getRouteErrorView(routeError);

  useEffect(() => {
    if (view !== 'error') return;
    const error = toError(routeError);
    logger.error('Route error', { error: error.message, stack: error.stack });
    errorHandlingService.handleError(error, { context: 'RouteErrorElement', showToast: false });
  }, [view, routeError]);

  if (view === 'not-found') {
    return (
      <WrapperLayout>
        <NotFoundPage />
      </WrapperLayout>
    );
  }

  // A failed lazy chunk after a deploy hits the same stale service-worker cache on a plain reload.
  const recover = () => {
    if (getRouteErrorRecovery(routeError) === 'clear-caches-and-reload') {
      void clearCachesAndReload();
    } else {
      window.location.reload();
    }
  };

  return (
    <GlobalErrorFallback
      error={toError(routeError)}
      onReset={recover}
      onRefresh={recover}
      onGoHome={() => {
        window.location.href = '/';
      }}
    />
  );
};
